<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import CustomerEmptyState from '@/V2/customers/CustomerEmptyState.vue';
import { mapGetters } from 'vuex';
import {
  CUSTOMER_SIGNAL_CONFIG,
  CUSTOMER_SIGNAL,
  UNIFIED_AGENT_RESULT_TO_OUTCOME_MAP,
} from '@/constants/signals/customer-signals.constants';
import { RUN_STATUS } from '@/constants/analyst-constants';
import CustomerListLoadingBars from '@/V2/customers/CustomerListLoadingBars.vue';
import { iFeaturedIconTypes, iFeaturedIcon } from '@inscribe/signal';
import CustomerFraudSignalsDetails from './CustomerFraudSignalsDetails.vue';
import {
  SEVERITY_LEVEL_BY_FRAUD_SIGNAL,
  SEVERITY_LEVEL,
} from '@/constants/signalNames';
import {
  mapVerificationResultToIcon,
  mapVerificationResultToStatus,
} from '@/V2/components/status/utils';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import { RISKS_CONFIG } from '@/constants/analyst-config';
import { SALARY_CROSS_CHECK_PROPERTIES } from '@/constants/signals/customer-signals.constants';

export default {
  components: {
    ExpandableContainer,
    ExpandableContainerItem,
    iFeaturedIcon,
    CustomerFraudSignalsDetails,
    CustomerEmptyState,
    StatusBadge,
    CustomerListLoadingBars,
  },
  props: {
    latestCustomerRun: {
      type: Object,
    },
    showLoadingState: {
      type: Boolean,
    },
  },
  data() {
    return {
      iFeaturedIconTypes,
      iDrawerVisible: false,
      selectedSignal: null,
      RISKS_CONFIG,
    };
  },
  computed: {
    ...mapGetters([
      'getCachedCustomerSignalsById',
      'getCachedAggregatedDocumentResults',
    ]),
    showEmptyState() {
      return !this.showLoadingState && !this.customerSignals.length;
    },
    analysisHasRun() {
      return (
        this.latestCustomerRun &&
        this.latestCustomerRun.status === RUN_STATUS.FINISHED
      );
    },
    customerSignals() {
      const returnSignals = [];
      const rawSignals =
        this.getCachedCustomerSignalsById(this.$route.params.customer_id)
          ?.data ?? {};
      Object.keys(rawSignals || {}).forEach((signal) => {
        const signalConfig = CUSTOMER_SIGNAL_CONFIG[signal];

        if (signalConfig) {
          let signalObject = {
            key: signal,
            ...signalConfig,
            name: signal,
            value: rawSignals[signal],
            icon: 'medium_solid_alert_triangle',
            status: 'error',
          };

          // verifications
          if (signalConfig.isVerification) {
            signalObject.hasBadge = true;
            signalObject.icon = mapVerificationResultToIcon(
              rawSignals[signal].result,
            );
            signalObject.status = mapVerificationResultToStatus(
              rawSignals[signal].result,
            );
            signalObject.badgeLabel =
              RISKS_CONFIG[rawSignals[signal].result].label;
          }

          // salary cross check
          if (signal === CUSTOMER_SIGNAL.SALARY_CROSS_CHECK) {
            signalObject.hasBadge = true;
            signalObject.icon =
              SALARY_CROSS_CHECK_PROPERTIES[signalObject.value.outcome]?.icon ||
              SALARY_CROSS_CHECK_PROPERTIES[
                UNIFIED_AGENT_RESULT_TO_OUTCOME_MAP[signalObject.value.result]
              ]?.icon;
            signalObject.status =
              SALARY_CROSS_CHECK_PROPERTIES[signalObject.value.outcome]
                ?.status ||
              SALARY_CROSS_CHECK_PROPERTIES[
                UNIFIED_AGENT_RESULT_TO_OUTCOME_MAP[signalObject.value.result]
              ]?.status;
            signalObject.description = signalObject.value.rationale;
            signalObject.badgeLabel =
              SALARY_CROSS_CHECK_PROPERTIES[signalObject.value.outcome]
                ?.label ||
              SALARY_CROSS_CHECK_PROPERTIES[
                UNIFIED_AGENT_RESULT_TO_OUTCOME_MAP[signalObject.value.result]
              ]?.label;
          }

          if (signal === CUSTOMER_SIGNAL.MULTIPLE_DOCUMENT_RISK_SIGNALS) {
            // add just if there are documents with high risk
            const hasHighSeverity = Object.keys(
              this.aggregatedDocumentsResults?.fraud_results || {},
            ).some((signalKey) => {
              return (
                SEVERITY_LEVEL_BY_FRAUD_SIGNAL[signalKey] ===
                SEVERITY_LEVEL.HIGH
              );
            });

            if (hasHighSeverity) {
              returnSignals.push(signalObject);
            }
          } else {
            returnSignals.push(signalObject);
          }
        }
      });

      returnSignals.sort((a, b) => a.sortOrder - b.sortOrder);

      return returnSignals;
    },
    aggregatedDocumentsResults() {
      return this.getCachedAggregatedDocumentResults(
        this.$route.params.customer_id,
      );
    },
  },
  methods: {
    onSignalClick(signal) {
      this.selectedSignal = signal;
      this.iDrawerVisible = true;
    },
    hideDrawer() {
      this.iDrawerVisible = false;
    },
  },
};
</script>
<template>
  <div class="customer-fraud-signals">
    <ExpandableContainer
      title="Customer Insights"
      :subtitle="`${customerSignals.length} detected`"
    >
      <template #items>
        <div class="slot-content">
          <!-- loading state -->
          <customer-list-loading-bars
            v-if="showLoadingState"
            data-testid="customer-fraud-signals-loading"
          >
          </customer-list-loading-bars>

          <!-- empty state -->
          <customer-empty-state
            v-else-if="showEmptyState"
            title="No Customer Insights"
            :description="
              analysisHasRun
                ? 'Analysis complete. No additional insights at the customer level'
                : 'Customer-level signals will appear here'
            "
            icon="medium_user_single"
            :show-button="false"
            data-testid="customer-fraud-signals-empty-state"
          />

          <!-- loaded state -->
          <div v-else class="signals-container d-flex flex-column">
            <expandable-container-item
              v-for="signal in customerSignals"
              :key="signal.name"
              :title="signal.label"
              :subtitle="
                signal.isVerification ? signal.value?.title : signal.description
              "
              @on-item-click="onSignalClick(signal)"
              @on-right-icon-click="onSignalClick(signal)"
              data-testid="customer-fraud-signal-item"
            >
              <template #left-container>
                <i-featured-icon
                  :symbol="signal.icon"
                  :type="signal.status"
                ></i-featured-icon>
              </template>

              <template #right-container>
                <status-badge
                  v-if="signal.hasBadge"
                  :state="signal.status"
                  :label="signal.badgeLabel"
                ></status-badge>
              </template>
            </expandable-container-item>
          </div>
        </div>
      </template>
    </ExpandableContainer>

    <customer-fraud-signals-details
      v-if="!showLoadingState"
      :iDrawerVisible="iDrawerVisible"
      :selected-signal="selectedSignal"
      :aggregated-documents-results="aggregatedDocumentsResults?.fraud_results"
      @hide-drawer="hideDrawer"
    />
  </div>
</template>

<style lang="scss">
.customer-fraud-signals {
  .signals-container {
    gap: 1px;
    .signal-item {
      background: $white-base;
      width: 100%;
      height: 72px;
    }
  }
}
</style>
